"use client";
import { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { decodeReturnContext, withReturn, type ReturnContext, RETURN_PARAM } from "./return-context";

/**
 * Reads the inbound `?from=…` param, decodes the operator's
 * originating context, and returns helpers to:
 *   - render a back-link to where they came from
 *   - forward the same context onto downstream links so the
 *     chain (Inbox → Issue → Patch) keeps a single canonical
 *     source of truth: the original queue.
 */
export function useReturnContext(): {
  context: ReturnContext | null;
  /** Forward the inbound context onto a new link. */
  forward: (href: string) => string;
  /** Same as `forward` but accepts a fallback context if there
   *  was no inbound `from` (e.g. forwarding from Issues itself). */
  forwardOr: (href: string, fallback: ReturnContext | null) => string;
} {
  const params = useSearchParams();
  const raw = params.get(RETURN_PARAM);

  return useMemo(() => {
    const context = decodeReturnContext(raw);
    return {
      context,
      forward: (href: string) =>
        context ? withReturn(href, context) : href,
      forwardOr: (href: string, fallback: ReturnContext | null) => {
        const ctx = context ?? fallback;
        return ctx ? withReturn(href, ctx) : href;
      },
    };
  }, [raw]);
}

/**
 * Compose a ReturnContext that points at the *current* page (its
 * pathname + full search string), so an outgoing link can carry the
 * exact filter/page/selected state of the surface the operator is
 * leaving. Strips `from` from the search string to avoid nesting
 * the chain unboundedly — the current `from` (if any) becomes the
 * destination's `from`, not a doubly-wrapped one.
 */
export function useCurrentReturnContext(label: string): ReturnContext {
  const pathname = usePathname();
  const params = useSearchParams();
  return useMemo(() => {
    // Drop any inbound `from` param when describing "current page"
    // — otherwise the chain becomes /a → /b?from=a&from=a (loops).
    const cleaned = new URLSearchParams(params.toString());
    cleaned.delete(RETURN_PARAM);
    const search = cleaned.toString();
    return {
      label,
      href: search ? `${pathname}?${search}` : pathname,
    };
  }, [label, pathname, params]);
}

/**
 * Convenience: produce a `forward(href)` function bound to the
 * current page as the implicit return context. For outgoing links
 * from a list/queue surface ("Inbox" → "/issues/BIZ-12").
 */
export function useForwardWithReturn(label: string): (href: string) => string {
  const ctx = useCurrentReturnContext(label);
  return useMemo(() => (href: string) => withReturn(href, ctx), [ctx]);
}
